import {
  EmptyStatePage,
  GlassCard,
  Glyph,
  makeEntrance,
  usePrefersReducedMotion,
} from "@/components/application/empty-state-page";

const CARD_SHADOW =
  "shadow-[inset_0_1px_0_0_rgb(255_255_255/0.28),inset_0_-1px_0_0_rgb(255_255_255/0.06),0_18px_44px_-16px_rgb(0_0_0/0.65)]";

const TARGET_ICON =
  "M16 8V5l3-3 1 2 2 1-3 3h-3Zm0 0-4 4m10 0c0 5.523-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2m5 10a5 5 0 1 1-5-5";
const INBOX_ICON =
  "M2.5 12h3.382c.685 0 1.312.387 1.618 1 .306.613.933 1 1.618 1h5.764c.685 0 1.312-.387 1.618-1 .306-.613.933-1 1.618-1H21.5M8.967 4h6.066c1.077 0 1.616 0 2.091.164a3 3 0 0 1 1.121.693c.36.352.6.833 1.082 1.796l2.166 4.333c.19.378.284.567.35.765.06.177.102.357.128.541.029.207.029.418.029.841V15.2c0 1.68 0 2.52-.327 3.162a3 3 0 0 1-1.311 1.311C19.72 20 18.88 20 17.2 20H6.8c-1.68 0-2.52 0-3.162-.327a3 3 0 0 1-1.311-1.311C2 17.72 2 16.88 2 15.2v-2.067c0-.422 0-.634.029-.84.026-.184.068-.365.128-.541.066-.199.16-.388.35-.766l2.166-4.333c.482-.963.723-1.444 1.082-1.796a3 3 0 0 1 1.12-.693C7.352 4 7.89 4 8.968 4Z";

type CollabRow = { name: string; sub: string; trailing: string };

type CollabVariant = {
  eyebrow: string;
  headline: string;
  subline: string;
  portraitSrc: string;
  portraitWidthClassName: string;
  cardTitle: string;
  icon: string;
  rows: CollabRow[];
  primaryLabel?: string;
};

const VARIANTS: Record<"target" | "open", CollabVariant> = {
  target: {
    eyebrow: "Target",
    headline: "Hand-pick who sells your products",
    subline:
      "Create a collab and invite the creators you want on it. Every invite lands here with its stage attached, from invited to sample sent, posted and GMV generated.",
    portraitSrc: "/empty-states/portrait-target.webp",
    portraitWidthClassName: "w-full",
    cardTitle: "Creators worth targeting",
    icon: TARGET_ICON,
    rows: [
      { name: "@ella.skincare", sub: "Beauty & Skincare · 84K followers", trailing: "96% match" },
      { name: "@marcusdoes", sub: "Home & Kitchen · 52K followers", trailing: "91% match" },
      { name: "@thatglowgirl", sub: "Beauty & Skincare · 130K followers", trailing: "88% match" },
    ],
    primaryLabel: "Create a collab",
  },
  open: {
    eyebrow: "Open",
    headline: "Open your products to any creator",
    subline:
      "Set a product and a commission, and any creator on TikTok can pick it up and start selling. Everything you’ve opened shows here, along with who’s taken it.",
    portraitSrc: "/empty-states/portrait-open.webp",
    portraitWidthClassName: "w-[65%]",
    cardTitle: "Open collaboration requests",
    icon: INBOX_ICON,
    rows: [
      { name: "@nina.beautyy", sub: "Requested — Hydrating Serum 30ml", trailing: "New" },
      { name: "@leo.reviews", sub: "Requested — Matte Lip Set", trailing: "New" },
      { name: "@thecleanroutine", sub: "Requested — Cleansing Balm", trailing: "2d ago" },
    ],
  },
};

const ROW_DELAYS = [0.6, 0.78, 0.96];

type CollabsListEmptyStateProps = {
  variant: "target" | "open";
  onCreate?: () => void;
};

/** Shared shop-gated empty state for the Collabs Target and Open tabs. */
export function CollabsListEmptyState({ variant, onCreate }: CollabsListEmptyStateProps) {
  const reduceMotion = usePrefersReducedMotion();
  const entrance = makeEntrance(reduceMotion);
  const content = VARIANTS[variant];

  return (
    <EmptyStatePage
      eyebrow={content.eyebrow}
      headline={content.headline}
      subline={content.subline}
      measureCh={46}
      portraitSrc={content.portraitSrc}
      portraitMaskLeft="34%"
      portraitMaskBottom="78%"
      portraitClassName={`top-1/2 -right-20 h-auto ${content.portraitWidthClassName} -translate-y-1/2 opacity-55`}
      actionsDelaySeconds={1.26}
      primaryAction={content.primaryLabel && onCreate ? { label: content.primaryLabel, onClick: onCreate } : undefined}
      device={
        <div className="w-full max-w-[560px]">
          <GlassCard
            radius="2xl"
            className={`w-full p-6 backdrop-blur-3xl ${CARD_SHADOW}`}
            style={{ animation: entrance.glassIn(0.35) }}
            overlayStyle={{ animation: entrance.fadeIn(0.35) }}
          >
            <div className="flex items-center justify-between gap-4" style={{ animation: entrance.fadeInUp(0.35) }}>
              <p className="text-base leading-6 text-white">{content.cardTitle}</p>
              <span aria-hidden="true" className="shrink-0 text-white/70">
                <Glyph d={content.icon} />
              </span>
            </div>
            <div className="mt-5 flex flex-col">
              {content.rows.map((row, index) => (
                <div
                  key={row.name}
                  className="flex items-center gap-4 border-t border-white/12 py-3 first:border-t-0 first:pt-0"
                  style={{
                    animation: reduceMotion ? undefined : `fade-in-up 0.4s ease-out ${ROW_DELAYS[index]}s both`,
                  }}
                >
                  <div className="flex min-w-0 flex-1 flex-col">
                    <p className="truncate text-sm font-semibold text-white">{row.name}</p>
                    <p className="text-xs text-brand-100">{row.sub}</p>
                  </div>
                  <span className="shrink-0 text-sm font-semibold text-white">{row.trailing}</span>
                </div>
              ))}
            </div>
          </GlassCard>
        </div>
      }
    />
  );
}
